/**
 * ImportPhysiciansModal Component
 *
 * Super-admin modal for importing physicians from another clinic into the
 * current clinic via the draft store. Mirrors ImportPharmaciesModal.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge
} from './ui'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'

export default function ImportPhysiciansModal({
  isOpen,
  onClose,
  currentClinicId,
  onImport
}) {
  const { user, getAvailableClinics } = useAuthStore()
  const [availableClinics, setAvailableClinics] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState('')
  const [physicians, setPhysicians] = useState([])
  const [selectedPhysicians, setSelectedPhysicians] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen && user) {
      loadAvailableClinics()
    }
  }, [isOpen, user])

  useEffect(() => {
    if (selectedClinicId) {
      loadPhysicians(selectedClinicId)
    }
  }, [selectedClinicId])

  useEffect(() => {
    if (isOpen && user && currentClinicId) {
      const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = useDraftStore.getState()
      const userId = user.id

      if (!isInitialized || storeUserId !== userId || storeClinicId !== currentClinicId) {
        console.log('[IMPORT PHYSICIANS] Initializing draft store with:', { clinicId: currentClinicId, userId })
        initialize(currentClinicId, userId)
      }
    }
  }, [isOpen, user, currentClinicId])

  const loadAvailableClinics = () => {
    try {
      const allClinics = getAvailableClinics()
      const filtered = allClinics.filter(c => c.id !== currentClinicId)
      setAvailableClinics(filtered)
      console.log('[IMPORT PHYSICIANS] Available clinics for import:', filtered)
    } catch (err) {
      console.error('[IMPORT PHYSICIANS] Error loading clinics:', err)
      setAvailableClinics([])
    }
  }

  const fetchAllPhysicians = async (params) => {
    const all = []
    let page = 1
    const PAGE_LIMIT = 100
    const MAX_PAGES = 50
    while (page <= MAX_PAGES) {
      const response = await api.get('/physicians', {
        params: { ...params, limit: PAGE_LIMIT, page }
      })
      const pageList = response.data?.data?.physicians || []
      all.push(...pageList)
      const pagination = response.data?.data?.pagination
      if (!pagination?.has_next_page || pageList.length === 0) break
      page += 1
    }
    return all
  }

  const loadPhysicians = async (clinicId) => {
    try {
      setLoading(true)
      setError(null)

      const [sourceList, existingList] = await Promise.all([
        fetchAllPhysicians({ source_clinic: clinicId }),
        currentClinicId ? fetchAllPhysicians({}) : Promise.resolve([])
      ])

      const existingNpis = new Set(
        existingList.map(p => p?.npi).filter(Boolean).map(String)
      )

      const filtered = sourceList.filter(p => {
        const npi = p?.npi != null ? String(p.npi) : null
        return !npi || !existingNpis.has(npi)
      })

      const skippedCount = sourceList.length - filtered.length
      console.log(
        `[IMPORT PHYSICIANS] Source ${sourceList.length} • already in current clinic ${skippedCount} • importable ${filtered.length}`
      )

      setPhysicians(filtered)
      setSelectedPhysicians(new Set())
    } catch (err) {
      console.error('[IMPORT PHYSICIANS] Error loading physicians:', err)
      const errorMsg = err.response?.status === 404
        ? `Clinic "${clinicId}" not found or has no physician data available.`
        : err.response?.status === 403
        ? `You don't have permission to access clinic "${clinicId}".`
        : err.response?.data?.message || 'Failed to load physicians from selected clinic.'
      setError(errorMsg)
      setPhysicians([])
    } finally {
      setLoading(false)
    }
  }

  const togglePhysician = (physicianId) => {
    setSelectedPhysicians(prev => {
      const next = new Set(prev)
      if (next.has(physicianId)) {
        next.delete(physicianId)
      } else {
        next.add(physicianId)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedPhysicians(new Set(physicians.map(p => p._id)))
  }

  const deselectAll = () => {
    setSelectedPhysicians(new Set())
  }

  const handleImport = async () => {
    if (selectedPhysicians.size === 0) {
      alert('Please select at least one physician to import')
      return
    }

    try {
      setImporting(true)
      const { addChange } = useDraftStore.getState()

      const selected = physicians.filter(p => selectedPhysicians.has(p._id))

      for (const physician of selected) {
        const {
          _id,
          created_at,
          updated_at,
          created_by,
          updated_by,
          display_name,
          active_licenses,
          expiring_licenses,
          ...physicianData
        } = physician

        const fullName = display_name || [physician.prefix, physician.first_name, physician.last_name].filter(Boolean).join(' ')

        addChange({
          resource_type: 'physician',
          resource_id: physician.npi || `import_${Date.now()}_${Math.random()}`,
          operation: 'create',
          original_data: null,
          new_data: physicianData,
          description: `Import physician: ${fullName} from ${selectedClinicId}`
        })
      }

      console.log(`[IMPORT PHYSICIANS] Added ${selectedPhysicians.size} physicians to drafts`)

      onImport?.()
      onClose()
      alert(`Successfully added ${selectedPhysicians.size} physicians to drafts. Publish a release to apply the changes.`)
    } catch (err) {
      console.error('[IMPORT PHYSICIANS] Error importing physicians:', err)
      alert('Failed to import physicians. Please try again.')
    } finally {
      setImporting(false)
    }
  }

  const handleClose = () => {
    setSelectedClinicId('')
    setPhysicians([])
    setSelectedPhysicians(new Set())
    setError(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Physicians from Another Clinic</DialogTitle>
          <DialogDescription>
            Select a clinic to view their physicians, then choose which ones to import.
            Changes will be saved as drafts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Source Clinic
              {availableClinics.length === 0 && (
                <span className="text-muted-foreground font-normal ml-2">
                  (No other clinics available)
                </span>
              )}
            </label>
            <Select
              value={selectedClinicId}
              onValueChange={setSelectedClinicId}
              disabled={availableClinics.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={availableClinics.length === 0 ? 'No clinics available' : 'Choose a clinic...'} />
              </SelectTrigger>
              <SelectContent>
                {availableClinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.displayName || clinic.name || clinic.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableClinics.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                You don't have access to any other clinics, or you're already viewing all available clinics.
              </p>
            )}
          </div>

          {physicians.length > 0 && (
            <div className="flex items-center justify-between border-b pb-2">
              <div className="text-sm text-muted-foreground">
                {selectedPhysicians.size} of {physicians.length} selected
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  Deselect All
                </Button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading && (
              <div className="p-8 text-center text-muted-foreground">
                Loading physicians...
              </div>
            )}

            {error && (
              <div className="p-8 text-center">
                <div className="text-destructive font-medium mb-2">Error Loading Physicians</div>
                <div className="text-sm text-muted-foreground">{error}</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => loadPhysicians(selectedClinicId)}
                >
                  Try Again
                </Button>
              </div>
            )}

            {!loading && !error && !selectedClinicId && (
              <div className="p-8 text-center text-muted-foreground">
                Select a clinic to view available physicians
              </div>
            )}

            {!loading && !error && selectedClinicId && physicians.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                No physicians found in selected clinic
              </div>
            )}

            {!loading && !error && physicians.length > 0 && (
              <div className="divide-y">
                {physicians.map(physician => {
                  const fullName = physician.display_name
                    || [physician.prefix, physician.first_name, physician.last_name].filter(Boolean).join(' ')
                  const stateBadges = (physician.active_licenses || []).map(l => l.state).filter(Boolean)
                  return (
                    <label
                      key={physician._id}
                      className="flex items-start gap-3 p-4 hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPhysicians.has(physician._id)}
                        onChange={() => togglePhysician(physician._id)}
                        className="mt-1 w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{fullName}</div>
                        <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                          {physician.npi && <div>NPI: {physician.npi}</div>}
                          {physician.email && <div>{physician.email}</div>}
                          {physician.title && <div>{physician.title}</div>}
                          {stateBadges.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {stateBadges.map(state => (
                                <Badge key={state} variant="secondary" className="text-xs">
                                  {state}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || selectedPhysicians.size === 0}
          >
            {importing ? 'Importing...' : `Import ${selectedPhysicians.size} Physicians to Drafts`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
